import { Workbox } from 'workbox-window';
import { logError } from './logger';

/**
 * Enregistre le Service Worker buildé par vite-plugin-pwa.
 *
 * - En production : `/sw.js` est généré au build et enregistré ici
 * - En dev : `devOptions.enabled = false` dans vite.config → no-op
 *
 * Stratégie d'update : le nouveau SW prend le contrôle tout de suite (skipWaiting +
 * clients.claim dans sw.ts), mais la page ne se recharge PAS sur le coup. Avant le
 * 16/09/2026 elle rechargeait dès `controlling` : chaque déploiement (plusieurs par
 * jour) rechargeait l'app en pleine lecture ou rédaction d'un mail. On marque la mise
 * à jour en attente et on recharge au prochain changement de page
 * (`useDeferredAppUpdate`), un moment que l'utilisateur a lui-même choisi.
 */

let updatePending = false;

/** Une nouvelle version contrôle la page : la recharger au prochain changement de page. */
export function isAppUpdatePending(): boolean {
  return updatePending;
}

/** Posé par le cycle de vie du SW ci-dessous ; exporté pour tester le rechargement différé. */
export function markAppUpdatePending(): void {
  updatePending = true;
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.DEV) return; // SW désactivé en dev

  const wb = new Workbox('/sw.js', { scope: '/' });

  wb.addEventListener('waiting', () => {
    // Le nouveau SW est prêt, on lui demande de prendre le relais
    void wb.messageSkipWaiting();
  });

  // Un SW contrôlait-il déjà la page ? Sinon le premier `controlling` est l'installation
  // (première visite, rechargement forcé) ; tout suivant est un déploiement.
  let controlled = Boolean(navigator.serviceWorker.controller);
  wb.addEventListener('controlling', () => {
    if (controlled) markAppUpdatePending();
    controlled = true;
  });

  wb.register().catch((err: unknown) => {
    // En cas d'erreur d'enregistrement, on ne crashe pas l'app — le SW est best-effort
    logError('sw', 'registration failed', err);
  });
}
